/**
 * track.ts — typed track() client.
 *
 * The single entry point for all behavior telemetry in gg-web. Composes:
 *   - DNT/GPC check (suppress if set)
 *   - Envelope auto-attachment (sessionId, appVersion, platform, ts, routeTemplate, online)
 *   - Typed props per the 15-event taxonomy
 *   - Durable queue (enqueueEvent)
 *
 * Contract (§10 atkinson-discretion):
 *   - session-only in-memory id (never persisted, never the auth UID)
 *   - DNT/GPC honored (suppress all 3 streams)
 *   - path-templating before send
 *   - swallow all responses/errors (telemetry must NEVER affect app behavior)
 *   - mirror wire types exactly
 *   - chunk flush to caps (handled in queue.ts)
 *
 * Capacitor readiness:
 *   - No direct firebase/* import — auth.currentUser not used here.
 *   - navigator.onLine NOT used directly — imported from useOnlineStatus seam
 *     is the right approach for components; here we read navigator.onLine
 *     transiently for the event envelope. This is the telemetry library layer,
 *     not a component — the abstraction boundary for Capacitor is the track()
 *     call site, not this implementation. The queue.ts flush uses auth from
 *     src/lib/firebase (the single allowed import point).
 */

import { isDntActive } from './dnt';
import { enqueueEvent } from './queue';
import { currentRouteTemplate } from './route';
import type { BehaviorEvent, BehaviorEventName, BehaviorEventPropsMap } from './types';

// ---------------------------------------------------------------------------
// Online status — transient read for envelope stamping only.
// NOTE: components should use useOnlineStatus() for rendering decisions.
// This is the telemetry layer reading it for metadata purposes only.
// ---------------------------------------------------------------------------

function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

// ---------------------------------------------------------------------------
// Core track() function — typed via the BehaviorEventPropsMap
// ---------------------------------------------------------------------------

/**
 * Track a behavior event. Type-safe: TypeScript enforces that `props` matches
 * the shape required by `name`.
 *
 * No-ops silently if:
 *   - DNT or GPC is active
 *   - name is not one of the 15 allowed events (TypeScript catches this at
 *     compile time, but the runtime guard handles dynamic calls)
 *
 * Swallows all errors — telemetry must NEVER affect app behavior.
 */
export function track<N extends BehaviorEventName>(name: N, props: BehaviorEventPropsMap[N]): void {
  // Telemetry must NEVER throw into the call site.
  try {
    if (isDntActive()) return;

    const event: BehaviorEvent = {
      type: 'behavior',
      ts: new Date().toISOString(),
      routeTemplate: currentRouteTemplate(),
      online: isOnline(),
      name,
      // props is correctly typed by the generic constraint. The cast is needed
      // because TypeScript can't narrow the union member type through the generic.
      props: props as BehaviorEvent['props'],
    };

    // Fire-and-forget — do not await; do not propagate errors.
    void enqueueEvent(event).catch(() => {
      /* swallow */
    });
  } catch {
    // Final safety net — should never be reached but guarantees no throw.
  }
}

// ---------------------------------------------------------------------------
// Bucket helpers — exported so call sites compute correct labels
// ---------------------------------------------------------------------------

/**
 * Convert a number of days (e.g. task age) to the contracted bucket label.
 * §5: client does the bucketing; server does no math.
 */
export type AgeDaysBucket = '0' | '1-7' | '8-30' | '31-90' | '90+';

export function bucketDays(days: number): AgeDaysBucket {
  if (days <= 0) return '0';
  if (days <= 7) return '1-7';
  if (days <= 30) return '8-30';
  if (days <= 90) return '31-90';
  return '90+';
}
